import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Tooltip, tokensForThemeProvider} from '@forecast-it/design-system';

const tokens = tokensForThemeProvider.sky.sys;

/** A muted dash standing where a figure would be, with the reason one hover away. Muted rather than
 * absent: it has to be visible enough to invite the hover that carries the explanation, and quiet
 * enough not to read as a value.
 *
 * `body['2']` is the token the table already sets on its cells, so the dash matches the surrounding
 * cells rather than introducing a third size. */
export const MutedNote = styled.span`
	cursor: help;
	color: ${tokens.color.text.disabled.onlight};
	${tokens.font.body['2']}
`;

/** An estimate nobody has set: a muted dash whose tooltip and accessible name say "Not estimated",
 * never a `0` that reads as an estimate of zero. `detailsId` picks the supporting sentence - one
 * task's cell, or a total none of whose tasks has an estimate.
 *
 * The explanation is also the trigger's accessible name, because a tooltip alone is invisible to a
 * screen reader. */
export const UnestimatedNote = ({detailsId}: {detailsId: string}): JSX.Element => {
	const {formatMessage} = useIntl();
	const title = formatMessage({id: 'ai_report_builder.unestimated.title'});
	const details = formatMessage({id: detailsId});
	return (
		<Tooltip content={<Tooltip.Item title={title} details={details} />} interactable={false}>
			<MutedNote aria-label={`${title}. ${details}`} role="note">
				&mdash;
			</MutedNote>
		</Tooltip>
	);
};
